import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  MapPin, ArrowLeft, Edit2, Save, X, Loader2, Navigation,
  Maximize2, Search, Link, Info, Crosshair, ExternalLink, Eye, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix default Leaflet icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const greenMarkerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const redMarkerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

/** Free tile styles — no Google billing / API key required */
const TILE_STYLES = {
  streets: {
    id: 'streets',
    label: 'Streets',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, OpenStreetMap',
    maxZoom: 19,
  },
  voyager: {
    id: 'voyager',
    label: 'Voyager',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
    maxZoom: 20,
  },
  osm: {
    id: 'osm',
    label: 'OSM',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    subdomains: 'abc',
    maxZoom: 19,
  },
  satellite: {
    id: 'satellite',
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics',
    maxZoom: 19,
  },
};

function googleMapsOpenUrl(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}&z=17`;
}

function googleMapsEmbedUrl(lat, lng) {
  // Free embed preview (no Maps JavaScript API key). Open-in-tab is the reliable fallback.
  return `https://maps.google.com/maps?q=${lat},${lng}&z=17&output=embed`;
}

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'TracklyHRM/1.0' } }
    );
    const data = await res.json();
    return data.display_name || `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
  } catch {
    return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
  }
}

/** Parse "lat, lng" or a single number from an input string. */
function parseCoordInput(raw) {
  const text = String(raw ?? '').trim().replace(/\s+/g, ' ');
  if (!text) return { pair: null, single: null };

  // "31.5204, 74.3587" or "31.5204 74.3587"
  const pair = text.match(/^(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)$/);
  if (pair) {
    const lat = parseFloat(pair[1]);
    const lng = parseFloat(pair[2]);
    if (isFinite(lat) && isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { pair: { lat, lng }, single: null };
    }
  }

  const single = parseFloat(text);
  if (!isNaN(single) && isFinite(single)) return { pair: null, single };
  return { pair: null, single: null };
}

function isValidCoords(val) {
  return (
    val &&
    isFinite(Number(val.lat)) &&
    isFinite(Number(val.lng)) &&
    Number(val.lat) >= -90 &&
    Number(val.lat) <= 90 &&
    Number(val.lng) >= -180 &&
    Number(val.lng) <= 180
  );
}

function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;
      const address = await reverseGeocode(lat, lng);
      onLocationSelect({ lat, lng, address });
    },
  });
  return null;
}

/** Pan/zoom when lat+lng change — depends on primitives, not object identity. */
function MapRecenter({ lat, lng, zoom = 16 }) {
  const map = useMap();
  useEffect(() => {
    if (isFinite(lat) && isFinite(lng)) {
      map.setView([lat, lng], Math.max(map.getZoom(), zoom), { animate: true });
    }
  }, [lat, lng, zoom, map]);
  return null;
}

/** Fit map so pin + radius circle are fully visible. */
function FitRadius({ lat, lng, radius }) {
  const map = useMap();
  useEffect(() => {
    if (!isFinite(lat) || !isFinite(lng) || !radius) return;
    const bounds = L.latLng(lat, lng).toBounds(Math.max(radius * 2.4, 80));
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 18, animate: true });
  }, [lat, lng, radius, map]);
  return null;
}

function MapTiles({ styleId = 'streets' }) {
  const style = TILE_STYLES[styleId] || TILE_STYLES.streets;
  const props = {
    key: style.id,
    attribution: style.attribution,
    url: style.url,
    maxZoom: style.maxZoom ?? 19,
  };
  // Only pass subdomains when defined — passing undefined overrides Leaflet's default
  // and crashes in TileLayer._getSubdomain (...reading 'length')
  if (style.subdomains) {
    props.subdomains = style.subdomains;
  } else if (style.url.includes('{s}')) {
    props.subdomains = 'abc';
  }
  return <TileLayer {...props} />;
}

/** Keep marker + circle in sync when coords/radius change without remounting the map. */
function MapOverlay({ lat, lng, radius, color, markerIcon }) {
  if (!isFinite(lat) || !isFinite(lng)) return null;
  return (
    <>
      <Marker position={[lat, lng]} icon={markerIcon} key={`marker-${lat}-${lng}`} />
      <Circle
        center={[lat, lng]}
        radius={radius}
        pathOptions={{ color, fillColor: color, fillOpacity: 0.18, weight: 2.5 }}
      />
      <MapRecenter lat={lat} lng={lng} />
      <FitRadius lat={lat} lng={lng} radius={radius} />
    </>
  );
}

const RADIUS_OPTIONS = [
  { label: '50 m', value: 50 },
  { label: '100 m', value: 100 },
  { label: '200 m', value: 200 },
  { label: '500 m', value: 500 },
  { label: '1 km', value: 1000 },
  { label: '2 km', value: 2000 },
  { label: '5 km', value: 5000 },
];

const DEFAULT_CENTER = [51.505, -0.09];
const DEFAULT_RADIUS = 200;

function GPSAreaEditor({ type, color, value, onChange }) {
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fsRadius, setFsRadius] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [fsSearchQuery, setFsSearchQuery] = useState('');
  const [fsSearchResults, setFsSearchResults] = useState([]);
  const [fsSearching, setFsSearching] = useState(false);
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [resolvingUrl, setResolvingUrl] = useState(false);
  const [latText, setLatText] = useState(value?.lat != null ? String(value.lat) : '');
  const [lngText, setLngText] = useState(value?.lng != null ? String(value.lng) : '');
  const [locating, setLocating] = useState(false);
  const [tileStyle, setTileStyle] = useState('streets');
  const [mapView, setMapView] = useState('editor'); // 'editor' | 'google'
  const searchTimeout = useRef(null);
  const geocodeTimeout = useRef(null);

  const radius = value?.radius ?? DEFAULT_RADIUS;
  const circleColor = color === 'green' ? '#6366f1' : '#ef4444';
  const markerIcon = color === 'green' ? greenMarkerIcon : redMarkerIcon;
  const hasPin = isValidCoords(value);

  // Keep text fields in sync when map/search/extract updates value
  useEffect(() => {
    if (value?.lat != null && isFinite(Number(value.lat))) {
      setLatText(String(Number(value.lat)));
    }
    if (value?.lng != null && isFinite(Number(value.lng))) {
      setLngText(String(Number(value.lng)));
    }
  }, [value?.lat, value?.lng]);

  const applyCoords = useCallback((lat, lng, extra = {}) => {
    const next = {
      ...value,
      lat,
      lng,
      radius: value?.radius ?? DEFAULT_RADIUS,
      ...extra,
    };
    // Update pin immediately so the map moves while typing/pasting
    onChange(next);
    clearTimeout(geocodeTimeout.current);
    geocodeTimeout.current = setTimeout(async () => {
      const address = await reverseGeocode(lat, lng);
      onChange({ ...next, address });
    }, 350);
  }, [onChange, value]);

  const searchLocation = async (query, setResults, setLoading) => {
    if (!query.trim() || query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': 'en', 'User-Agent': 'TracklyHRM/1.0' } }
      );
      const data = await res.json();
      setResults(
        data.map((r) => ({
          label: r.display_name,
          lat: parseFloat(r.lat),
          lng: parseFloat(r.lon),
        }))
      );
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInput = (q, setQ, setResults, setLoading) => {
    setQ(q);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchLocation(q, setResults, setLoading), 400);
  };

  const selectResult = (result, setQ, setResults, isFullscreen = false) => {
    onChange({
      lat: result.lat,
      lng: result.lng,
      address: result.label,
      radius: value?.radius ?? DEFAULT_RADIUS,
    });
    setQ(result.label.split(',')[0]);
    setResults([]);
    if (isFullscreen) setFsRadius(value?.radius ?? DEFAULT_RADIUS);
  };

  const resolveGoogleMapsUrl = async () => {
    if (!googleMapsUrl.trim()) return;
    setResolvingUrl(true);
    try {
      const res = await axios.post(
        `${API}/admin/gps/resolve-google-maps`,
        { url: googleMapsUrl },
        { withCredentials: true }
      );
      const { lat, lng } = res.data;
      const address = await reverseGeocode(lat, lng);
      onChange({ lat, lng, address, radius: value?.radius ?? DEFAULT_RADIUS });
      setGoogleMapsUrl('');
      setMapView('editor');
      toast.success(`Pin set: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not extract coordinates from this URL');
    } finally {
      setResolvingUrl(false);
    }
  };

  const handleMapClick = ({ lat, lng, address }) => {
    onChange({ ...value, lat, lng, address, radius: value?.radius ?? DEFAULT_RADIUS });
  };

  const commitCoordTexts = (nextLatText, nextLngText) => {
    const latParsed = parseCoordInput(nextLatText);
    const lngParsed = parseCoordInput(nextLngText);

    // Paste "lat, lng" into either field
    if (latParsed.pair) {
      setLatText(String(latParsed.pair.lat));
      setLngText(String(latParsed.pair.lng));
      applyCoords(latParsed.pair.lat, latParsed.pair.lng);
      return;
    }
    if (lngParsed.pair) {
      setLatText(String(lngParsed.pair.lat));
      setLngText(String(lngParsed.pair.lng));
      applyCoords(lngParsed.pair.lat, lngParsed.pair.lng);
      return;
    }

    const lat = latParsed.single;
    const lng = lngParsed.single;
    if (lat != null && lng != null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      applyCoords(lat, lng);
    } else if (lat != null && lat >= -90 && lat <= 90) {
      onChange({ ...value, lat, radius: value?.radius ?? DEFAULT_RADIUS });
    } else if (lng != null && lng >= -180 && lng <= 180) {
      onChange({ ...value, lng, radius: value?.radius ?? DEFAULT_RADIUS });
    }
  };

  const handleLatChange = (e) => {
    const raw = e.target.value;
    setLatText(raw);
    commitCoordTexts(raw, lngText);
  };

  const handleLngChange = (e) => {
    const raw = e.target.value;
    setLngText(raw);
    commitCoordTexts(latText, raw);
  };

  const handleRadiusChange = (r) => onChange({ ...value, radius: r });

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const address = await reverseGeocode(lat, lng);
        onChange({ ...value, lat, lng, address, radius: value?.radius ?? DEFAULT_RADIUS });
        toast.success(`Location found (±${Math.round(pos.coords.accuracy)}m accuracy)`);
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        const msgs = {
          1: 'Location access denied. Allow location in browser settings.',
          2: 'Location unavailable. Try clicking the map instead.',
          3: 'Location timed out. Try again or click the map.',
        };
        toast.error(msgs[err.code] || 'Could not get your location');
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const openFullscreen = () => {
    setFsRadius(value?.radius ?? DEFAULT_RADIUS);
    setFullscreenOpen(true);
  };

  const openInGoogleMaps = () => {
    if (!hasPin) {
      toast.error('Place a pin first');
      return;
    }
    window.open(googleMapsOpenUrl(value.lat, value.lng), '_blank', 'noopener,noreferrer');
  };

  const accentBtn =
    color === 'green'
      ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
      : 'bg-red-500 hover:bg-red-600 text-white';

  return (
    <div className="space-y-5">
      <div className="flex gap-2.5 rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-2.5 text-xs text-sky-900">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-sky-600" />
        <div>
          <p className="font-medium text-sky-800">100% free map stack</p>
          <p className="text-sky-700/80 mt-0.5 leading-relaxed">
            Interactive editor uses free Esri / CARTO / OSM tiles (no Google billing).
            Paste a Google Maps URL to extract exact coords for free. Use <strong>Preview in Google</strong> or <strong>Open in Google Maps</strong> to verify the same pin on Google.
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-gray-600 flex items-center gap-1.5 font-medium">
          <Link className="w-3.5 h-3.5" /> Import from Google Maps URL
        </Label>
        <div className="flex gap-2">
          <input
            type="url"
            value={googleMapsUrl}
            onChange={(e) => setGoogleMapsUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && resolveGoogleMapsUrl()}
            placeholder="https://maps.app.goo.gl/... or full Google Maps link"
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 placeholder:text-gray-400"
          />
          <Button
            type="button"
            onClick={resolveGoogleMapsUrl}
            disabled={resolvingUrl || !googleMapsUrl.trim()}
            className={`shrink-0 h-9 px-3.5 text-xs gap-1.5 ${accentBtn}`}
          >
            {resolvingUrl ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Resolving…</>
            ) : (
              <><MapPin className="w-3.5 h-3.5" /> Extract</>
            )}
          </Button>
        </div>
      </div>

      <div className="relative flex items-center">
        <div className="flex-1 border-t border-dashed border-gray-200" />
        <span className="px-3 text-[11px] uppercase tracking-wide text-gray-400 shrink-0">or enter manually</span>
        <div className="flex-1 border-t border-dashed border-gray-200" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-600 mb-1.5 block font-medium">Latitude</Label>
          <Input type="text" inputMode="decimal" placeholder="e.g. 31.520369" value={latText} onChange={handleLatChange} onBlur={() => commitCoordTexts(latText, lngText)} className="font-mono text-sm h-10" />
        </div>
        <div>
          <Label className="text-xs text-gray-600 mb-1.5 block font-medium">Longitude</Label>
          <Input type="text" inputMode="decimal" placeholder="e.g. 74.358749" value={lngText} onChange={handleLngChange} onBlur={() => commitCoordTexts(latText, lngText)} className="font-mono text-sm h-10" />
        </div>
      </div>
      <p className="text-[11px] text-gray-400 -mt-2">
        Tip: paste <span className="font-mono text-gray-500">lat, lng</span> into either field. Both values are required for the pin.
      </p>

      <div>
        <Label className="text-xs text-gray-600 mb-2 block font-medium">Allowed Radius</Label>
        <div className="flex flex-wrap gap-2">
          {RADIUS_OPTIONS.map((opt) => (
            <button key={opt.value} type="button" onClick={() => handleRadiusChange(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                radius === opt.value
                  ? color === 'green' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-red-500 text-white border-red-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}>{opt.label}</button>
          ))}
          <input type="number" min={10} max={50000} value={radius}
            onChange={(e) => handleRadiusChange(parseInt(e.target.value, 10) || DEFAULT_RADIUS)}
            className="w-24 px-2 py-1.5 text-xs border border-gray-200 rounded-full font-mono focus:outline-none focus:ring-1 focus:ring-indigo-400"
            placeholder="Custom" />
        </div>
        <p className="text-[11px] text-gray-400 mt-1.5">
          Staff must be within this radius to {type === 'checkin' ? 'check in' : 'check out'}.
        </p>
      </div>

      <div className="space-y-2">
        <div className="relative">
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-indigo-400/40 focus-within:border-indigo-400">
            {searching ? <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin shrink-0" /> : <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
            <input type="text" value={searchQuery}
              onChange={(e) => handleSearchInput(e.target.value, setSearchQuery, setSearchResults, setSearching)}
              placeholder="Search city, address, place…"
              className="flex-1 text-sm outline-none bg-transparent placeholder:text-gray-400" />
            {searchQuery && (
              <button type="button" onClick={() => { setSearchQuery(''); setSearchResults([]); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {searchResults.length > 0 && (
            <div className="absolute z-[2000] w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
              {searchResults.map((r, i) => (
                <button key={i} type="button" onClick={() => selectResult(r, setSearchQuery, setSearchResults)}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0 truncate" title={r.label}>
                  <span className="font-medium">{r.label.split(',')[0]}</span>
                  <span className="text-gray-400 text-xs ml-1">{r.label.split(',').slice(1, 3).join(',')}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            <button type="button" onClick={() => setMapView('editor')}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${mapView === 'editor' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              Edit map
            </button>
            <button type="button" onClick={() => {
              if (!hasPin) { toast.error('Place a pin first to preview in Google Maps'); return; }
              setMapView('google');
            }}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1 ${mapView === 'google' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              <Eye className="w-3 h-3" /> Preview in Google
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button type="button" variant="outline" size="sm" onClick={handleCurrentLocation} disabled={locating} className="h-8 text-xs gap-1.5">
              {locating ? <><Loader2 className="w-3 h-3 animate-spin" /> Locating…</> : <><Navigation className="w-3 h-3" /> My Location</>}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={openInGoogleMaps} disabled={!hasPin} className="h-8 text-xs gap-1.5">
              <ExternalLink className="w-3 h-3" /> Open in Google Maps
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={openFullscreen} className="h-8 text-xs gap-1.5" data-testid={`expand-map-${type}`}>
              <Maximize2 className="w-3 h-3" /> Full Screen
            </Button>
          </div>
        </div>

        {mapView === 'editor' && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500"><Layers className="w-3 h-3" /> Map style</span>
            {Object.values(TILE_STYLES).map((style) => (
              <button key={style.id} type="button" onClick={() => setTileStyle(style.id)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  tileStyle === style.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}>{style.label}</button>
            ))}
          </div>
        )}

        {mapView === 'editor' ? (
          <div className={`h-72 rounded-xl overflow-hidden border border-gray-200 shadow-sm relative ${fullscreenOpen ? 'invisible' : ''}`}>
            {!hasPin && (
              <div className="absolute inset-0 z-[500] pointer-events-none flex items-end justify-center pb-3">
                <span className="bg-white/90 text-xs text-gray-600 px-3 py-1.5 rounded-full shadow border border-gray-100">
                  No pin yet — enter lat &amp; lng, extract a Google link, or click the map
                </span>
              </div>
            )}
            <MapContainer center={hasPin ? [Number(value.lat), Number(value.lng)] : DEFAULT_CENTER} zoom={hasPin ? 16 : 3} style={{ height: '100%', width: '100%' }} key={`map-${type}`}>
              <MapTiles styleId={tileStyle} />
              <MapClickHandler onLocationSelect={handleMapClick} />
              {hasPin && (
                <MapOverlay lat={Number(value.lat)} lng={Number(value.lng)} radius={radius} color={circleColor} markerIcon={markerIcon} />
              )}
            </MapContainer>
          </div>
        ) : (
          <div className="h-72 rounded-xl overflow-hidden border border-gray-200 shadow-sm relative bg-gray-50">
            {hasPin ? (
              <>
                <iframe title="Google Maps preview" src={googleMapsEmbedUrl(value.lat, value.lng)} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
                <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 pointer-events-none">
                  <span className="pointer-events-auto bg-white/95 text-[11px] text-gray-700 px-2.5 py-1.5 rounded-lg shadow border">
                    Google preview of the same coordinates (verify only — edit on the free map)
                  </span>
                  <button type="button" onClick={openInGoogleMaps} className="pointer-events-auto inline-flex items-center gap-1 bg-white text-xs font-medium px-2.5 py-1.5 rounded-lg shadow border hover:bg-gray-50">
                    <ExternalLink className="w-3 h-3" /> Open tab
                  </button>
                </div>
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-gray-500">Place a pin first to preview in Google Maps</div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Crosshair className="w-3.5 h-3.5" />
            {hasPin ? (
              <span className="text-indigo-700 font-medium">Pin: {Number(value.lat).toFixed(6)}, {Number(value.lng).toFixed(6)}</span>
            ) : (
              <span>Click map or enter both coordinates to place pin</span>
            )}
          </div>
          {value?.address && <p className="text-xs text-gray-500 truncate max-w-md" title={value.address}>{value.address}</p>}
        </div>
      </div>

      <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
        <DialogContent className="max-w-[95vw] w-[1100px] h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <MapPin className={`w-4 h-4 ${color === 'green' ? 'text-indigo-600' : 'text-red-500'}`} />
              Select {type === 'checkin' ? 'Check-In' : 'Check-Out'} Location
              <span className="text-xs font-normal text-gray-400 ml-1">— click the map to pin</span>
            </DialogTitle>
          </DialogHeader>
          <div className="px-4 py-2 border-b bg-gray-50 shrink-0 space-y-2">
            <div className="relative">
              <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-indigo-400/40">
                {fsSearching ? <Loader2 className="w-4 h-4 text-gray-400 animate-spin shrink-0" /> : <Search className="w-4 h-4 text-gray-400 shrink-0" />}
                <input type="text" value={fsSearchQuery}
                  onChange={(e) => handleSearchInput(e.target.value, setFsSearchQuery, setFsSearchResults, setFsSearching)}
                  placeholder="Search city, address or place name…"
                  className="flex-1 text-sm outline-none bg-transparent placeholder:text-gray-400" />
              </div>
              {fsSearchResults.length > 0 && (
                <div className="absolute z-[2000] w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                  {fsSearchResults.map((r, i) => (
                    <button key={i} type="button" onClick={() => selectResult(r, setFsSearchQuery, setFsSearchResults, true)}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0">
                      <span className="font-medium">{r.label.split(',')[0]}</span>
                      <span className="text-gray-400 text-xs ml-2">{r.label.split(',').slice(1, 3).join(',')}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs text-gray-500 font-medium">Radius:</span>
              <div className="flex flex-wrap gap-1.5">
                {RADIUS_OPTIONS.map((opt) => (
                  <button key={opt.value} type="button" onClick={() => setFsRadius(opt.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      fsRadius === opt.value
                        ? color === 'green' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                    }`}>{opt.label}</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5 ml-auto">
                {Object.values(TILE_STYLES).map((style) => (
                  <button key={style.id} type="button" onClick={() => setTileStyle(style.id)}
                    className={`px-2 py-1 rounded-full text-[11px] font-medium border ${tileStyle === style.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}>
                    {style.label}
                  </button>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={openInGoogleMaps} disabled={!hasPin} className="h-7 text-xs gap-1">
                <ExternalLink className="w-3 h-3" /> Google Maps
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => {
                if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
                navigator.geolocation.getCurrentPosition(async (pos) => {
                  const lat = pos.coords.latitude;
                  const lng = pos.coords.longitude;
                  const address = await reverseGeocode(lat, lng);
                  onChange({ lat, lng, address, radius: fsRadius ?? DEFAULT_RADIUS });
                  toast.success(`Location found (±${Math.round(pos.coords.accuracy)}m)`);
                }, () => toast.error('Could not get location'), { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 });
              }} className="h-7 text-xs gap-1">
                <Navigation className="w-3 h-3" /> My Location
              </Button>
            </div>
          </div>
          <div className="flex-1 relative">
            <MapContainer center={hasPin ? [Number(value.lat), Number(value.lng)] : DEFAULT_CENTER} zoom={hasPin ? 16 : 3} style={{ height: '100%', width: '100%' }} key={`fs-map-${type}-${fullscreenOpen}`}>
              <MapTiles styleId={tileStyle} />
              <MapClickHandler onLocationSelect={async (loc) => { onChange({ ...loc, radius: fsRadius ?? DEFAULT_RADIUS }); }} />
              {hasPin && (
                <MapOverlay lat={Number(value.lat)} lng={Number(value.lng)} radius={fsRadius ?? radius} color={circleColor} markerIcon={markerIcon} />
              )}
            </MapContainer>
            {hasPin && (
              <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-md px-3.5 py-2.5 z-[1000] text-xs space-y-0.5 border">
                <div className="font-semibold text-gray-700 mb-1">Selected Location</div>
                <div><span className="text-gray-400">Lat: </span><span className="font-mono">{Number(value.lat).toFixed(6)}</span></div>
                <div><span className="text-gray-400">Lng: </span><span className="font-mono">{Number(value.lng).toFixed(6)}</span></div>
                <div><span className="text-gray-400">Radius: </span><span className="font-mono">{(fsRadius ?? radius) >= 1000 ? `${((fsRadius ?? radius)/1000).toFixed(1)} km` : `${fsRadius ?? radius} m`}</span></div>
                {value.address && <div className="text-gray-500 max-w-xs truncate" title={value.address}>{value.address}</div>}
              </div>
            )}
          </div>
          <DialogFooter className="px-4 py-3 border-t shrink-0 gap-2">
            <Button variant="outline" onClick={() => setFullscreenOpen(false)}>Close</Button>
            <Button onClick={() => { if (fsRadius !== null) onChange({ ...value, radius: fsRadius }); setFullscreenOpen(false); }}
              data-testid={`confirm-fullscreen-${type}`} className={accentBtn}>
              <Save className="w-4 h-4 mr-1.5" /> Confirm Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OpenCloseSwitch({ checked, onCheckedChange, testId, accent = 'indigo' }) {
  return (
    <div className="flex items-center gap-2.5" data-testid={testId}>
      <span className={`text-xs font-medium ${checked ? 'text-gray-400' : 'text-gray-700'}`}>Close</span>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className={
          accent === 'indigo'
            ? 'data-[state=checked]:bg-indigo-600'
            : 'data-[state=checked]:bg-red-500'
        }
      />
      <span className={`text-xs font-medium ${checked ? (accent === 'indigo' ? 'text-indigo-700' : 'text-red-600') : 'text-gray-400'}`}>
        Open
      </span>
    </div>
  );
}

export default function StaffGPSSettingsPage() {
  const { staffId } = useParams();
  const navigate = useNavigate();

  const [staffName, setStaffName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [checkinEnabled, setCheckinEnabled] = useState(false);
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);
  const [checkin, setCheckin] = useState(null);
  const [checkout, setCheckout] = useState(null);

  const [editingType, setEditingType] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [staffRes, gpsRes] = await Promise.all([
          axios.get(`${API}/admin/staff`, { withCredentials: true }),
          axios.get(`${API}/admin/staff/${staffId}/gps-coordinates`, { withCredentials: true }),
        ]);
        const staffMember = (staffRes.data || []).find((s) => s.staff_id === staffId);
        if (staffMember) setStaffName(staffMember.name);

        const gps = gpsRes.data;
        setCheckin(gps.checkin || null);
        setCheckout(gps.checkout || null);
        setCheckinEnabled(!!(gps.checkin?.lat && gps.checkin?.enabled !== false));
        setCheckoutEnabled(!!(gps.checkout?.lat && gps.checkout?.enabled !== false));
      } catch {
        toast.error('Failed to load GPS settings');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [staffId]);

  const startEdit = (type) => {
    setEditingType(type);
    const existing = type === 'checkin' ? checkin : checkout;
    setEditDraft(existing ? { ...existing } : { radius: DEFAULT_RADIUS });
  };

  const cancelEdit = () => {
    setEditingType(null);
    setEditDraft(null);
  };

  const saveEdit = () => {
    if (!isValidCoords(editDraft)) {
      toast.error('Please select a location on the map or enter valid latitude and longitude');
      return;
    }
    if (editingType === 'checkin') {
      setCheckin(editDraft);
      if (!checkinEnabled) setCheckinEnabled(true);
    } else {
      setCheckout(editDraft);
      if (!checkoutEnabled) setCheckoutEnabled(true);
    }
    setEditingType(null);
    setEditDraft(null);
    toast.success('Location applied — click Save Settings to persist');
  };

  const handleToggle = (type, next) => {
    if (type === 'checkin') {
      if (next && !isValidCoords(checkin)) {
        toast.error('Set check-in coordinates first (click Edit)');
        startEdit('checkin');
        return;
      }
      setCheckinEnabled(next);
    } else {
      if (next && !isValidCoords(checkout)) {
        toast.error('Set check-out coordinates first (click Edit)');
        startEdit('checkout');
        return;
      }
      setCheckoutEnabled(next);
    }
  };

  const handleSaveAll = async () => {
    if (checkinEnabled && !isValidCoords(checkin)) {
      toast.error('Check-In is Open but no coordinates are set. Click Edit and pick a location.');
      return;
    }
    if (checkoutEnabled && !isValidCoords(checkout)) {
      toast.error('Check-Out is Open but no coordinates are set. Click Edit and pick a location.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        checkin: isValidCoords(checkin) ? { ...checkin, enabled: checkinEnabled } : null,
        checkout: isValidCoords(checkout) ? { ...checkout, enabled: checkoutEnabled } : null,
      };
      await axios.put(`${API}/admin/staff/${staffId}/gps-coordinates`, payload, { withCredentials: true });
      toast.success('GPS settings saved successfully');
      navigate('/admin/staff');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save GPS settings');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (v) => (v != null && !isNaN(+v) ? (+v).toFixed(6) : '—');

  const CoordSummary = ({ val, open }) =>
    isValidCoords(val) ? (
      <div className="space-y-1">
        <div className="font-mono text-xs text-gray-700 space-y-0.5">
          <div>
            <span className="text-gray-400">Lat </span>
            {fmt(val.lat)}
            <span className="text-gray-300 mx-1.5">·</span>
            <span className="text-gray-400">Lng </span>
            {fmt(val.lng)}
          </div>
          {val.radius != null && (
            <div>
              <span className="text-gray-400">Radius </span>
              {val.radius >= 1000 ? `${(val.radius / 1000).toFixed(1)} km` : `${val.radius} m`}
            </div>
          )}
        </div>
        {val.address && (
          <p className="text-[11px] text-gray-400 truncate max-w-md" title={val.address}>
            {val.address}
          </p>
        )}
        {!open && (
          <span className="inline-flex text-[10px] uppercase tracking-wide text-gray-400 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded">
            Saved but closed
          </span>
        )}
      </div>
    ) : (
      <span className="text-gray-400 text-xs italic">Not set — click Edit to place a pin</span>
    );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-8">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/staff')} className="shrink-0 mt-0.5">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2 text-gray-900">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600">
              <MapPin className="w-5 h-5" />
            </span>
            GPS Coordinates
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Geofence check-in / check-out for{' '}
            <span className="font-medium text-gray-800">{staffName || 'staff'}</span>
          </p>
        </div>
      </div>

      {/* Config cards */}
      <div className="grid gap-4">
        {/* Check-In */}
        <Card className="overflow-hidden border-gray-200 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 px-5 py-4">
            <div className="flex items-center gap-3 min-w-[140px]">
              <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
              <div>
                <p className="font-semibold text-sm text-gray-900">Check-In</p>
                <p className="text-[11px] text-gray-400">Arrival geofence</p>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <CoordSummary val={checkin} open={checkinEnabled} />
            </div>
            <div className="flex items-center gap-4 sm:justify-end shrink-0">
              <OpenCloseSwitch
                checked={checkinEnabled}
                onCheckedChange={(v) => handleToggle('checkin', v)}
                testId="toggle-checkin-gps"
                accent="indigo"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => startEdit('checkin')}
                data-testid="edit-checkin-gps"
                className="h-8 text-xs gap-1.5"
              >
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </Button>
            </div>
          </div>
        </Card>

        {/* Check-Out */}
        <Card className="overflow-hidden border-gray-200 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 px-5 py-4">
            <div className="flex items-center gap-3 min-w-[140px]">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
              <div>
                <p className="font-semibold text-sm text-gray-900">Check-Out</p>
                <p className="text-[11px] text-gray-400">Departure geofence</p>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <CoordSummary val={checkout} open={checkoutEnabled} />
            </div>
            <div className="flex items-center gap-4 sm:justify-end shrink-0">
              <OpenCloseSwitch
                checked={checkoutEnabled}
                onCheckedChange={(v) => handleToggle('checkout', v)}
                testId="toggle-checkout-gps"
                accent="red"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => startEdit('checkout')}
                data-testid="edit-checkout-gps"
                className="h-8 text-xs gap-1.5"
              >
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <p className="text-xs text-gray-400 px-1">
        <span className="font-medium text-gray-500">Open</span> = GPS validation enforced at check-in/out.&nbsp;
        <span className="font-medium text-gray-500">Close</span> = coordinates kept but not enforced.
      </p>

      {/* Inline Editor */}
      {editingType && (
        <Card className={`border-2 shadow-sm ${editingType === 'checkin' ? 'border-indigo-400' : 'border-red-400'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${editingType === 'checkin' ? 'bg-indigo-500' : 'bg-red-500'}`} />
                Edit {editingType === 'checkin' ? 'Check-In' : 'Check-Out'} GPS Area
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={cancelEdit} className="h-7 w-7">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <CardDescription>
              Paste a Maps link, enter coordinates, search, or click the map. Apply then Save Settings.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <GPSAreaEditor
              type={editingType}
              color={editingType === 'checkin' ? 'green' : 'red'}
              value={editDraft}
              onChange={setEditDraft}
            />
            <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
              <Button variant="outline" onClick={cancelEdit} size="sm">
                Cancel
              </Button>
              <Button
                onClick={saveEdit}
                size="sm"
                data-testid={`confirm-edit-${editingType}`}
                className={
                  editingType === 'checkin'
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }
              >
                <Save className="w-3.5 h-3.5 mr-1.5" /> Apply
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save / Cancel */}
      <div className="flex justify-end gap-3 pt-1 sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent py-4">
        <Button variant="outline" onClick={() => navigate('/admin/staff')}>
          Back to Staff
        </Button>
        <Button
          onClick={handleSaveAll}
          disabled={saving}
          data-testid="save-gps-settings"
          className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-36"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Saving…
            </>
          ) : (
            'Save Settings'
          )}
        </Button>
      </div>
    </div>
  );
}
